import { useEffect, useRef } from "react";
import { cn } from "../../lib/cn";
import { formatTimeDisplay, parseTime24, toTime24 } from "../../utils/timeFormat";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const PERIODS = ["AM", "PM"];

function PickerColumn({ options, value, onChange, formatOption, disabled }) {
  const selectedRef = useRef(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "center" });
  }, [value]);

  return (
    <div
      className="h-40 overflow-y-auto overscroll-contain py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="listbox"
    >
      {options.map((option) => {
        const selected = option === value;
        const label = formatOption ? formatOption(option) : String(option);
        return (
          <button
            key={label}
            ref={selected ? selectedRef : undefined}
            type="button"
            role="option"
            aria-selected={selected}
            disabled={disabled}
            onClick={() => onChange(option)}
            className={cn(
              "mx-1 flex w-[calc(100%-0.5rem)] items-center justify-center rounded-md px-2 py-1.5 text-sm transition-colors",
              selected
                ? "bg-brand text-white font-semibold"
                : "text-foreground hover:bg-muted/70",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function TimeSelector({
  value = "09:00",
  onChange,
  disabled = false,
  className,
}) {
  const parsed = parseTime24(value);

  const updateTime = (next) => {
    onChange?.(toTime24(next.hour12, next.minute, next.period));
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-border bg-card",
        className,
      )}
    >
      <div className="grid grid-cols-[1fr_1fr_3.5rem] divide-x divide-border">
        <PickerColumn
          options={HOURS}
          value={parsed.hour12}
          disabled={disabled}
          formatOption={(hour) => String(hour).padStart(2, "0")}
          onChange={(hour12) => updateTime({ ...parsed, hour12 })}
        />
        <PickerColumn
          options={MINUTES}
          value={parsed.minute}
          disabled={disabled}
          formatOption={(minute) => String(minute).padStart(2, "0")}
          onChange={(minute) => updateTime({ ...parsed, minute })}
        />
        <PickerColumn
          options={PERIODS}
          value={parsed.period}
          disabled={disabled}
          onChange={(period) => updateTime({ ...parsed, period })}
        />
      </div>
    </div>
  );
}
